import { useState, useCallback } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { Plus } from 'lucide-react';
import TaskCard from './TaskCard';
import type { Task } from '../hooks/useTasks';
import { useUpdateTask, useCreateTask } from '../hooks/useTasks';
import type { Agent } from '../hooks/useAgents';
import type { Instance } from '../types/instance';

type TaskStatus = Task['status'];

const COLUMNS: { status: TaskStatus; label: string; color: string }[] = [
  { status: 'todo', label: 'Todo', color: 'bg-slate-400' },
  { status: 'in-progress', label: 'In Progress', color: 'bg-blue-500' },
  { status: 'paused', label: 'Paused', color: 'bg-yellow-500' },
  { status: 'in-review', label: 'In Review', color: 'bg-amber-500' },
  { status: 'complete', label: 'Complete', color: 'bg-green-500' },
];

interface TaskBoardProps {
  tasks: Task[];
  agents?: Agent[];
  instances?: Instance[];
  projectId: string;
}

function Column({ status, label, color, tasks, agents, instances, onAddTask }: {
  status: TaskStatus;
  label: string;
  color: string;
  tasks: Task[];
  agents?: Agent[];
  instances?: Instance[];
  onAddTask?: () => void;
}) {
  const { setNodeRef } = useDroppable({ id: status });
  const agentMap = new Map(agents?.map((a) => [a.id, a.name]));
  const instanceByTaskId = new Map(
    instances?.filter((i) => i.taskId).map((i) => [i.taskId!, i]) ?? [],
  );

  return (
    <div className="flex w-72 shrink-0 flex-col rounded-xl bg-slate-100 p-3">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`h-2.5 w-2.5 rounded-full ${color}`} />
          <span className="text-sm font-semibold text-slate-700">{label}</span>
          <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-600">
            {tasks.length}
          </span>
        </div>
        {onAddTask && (
          <button
            onClick={onAddTask}
            className="rounded p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-600"
          >
            <Plus className="h-4 w-4" />
          </button>
        )}
      </div>

      <div ref={setNodeRef} className="flex-1 space-y-2">
        <SortableContext items={tasks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
          {tasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              agentName={task.assignedAgentId ? agentMap.get(task.assignedAgentId) : undefined}
              instance={task.activeInstanceId ? instanceByTaskId.get(task.id) : undefined}
            />
          ))}
        </SortableContext>
      </div>
    </div>
  );
}

export default function TaskBoard({ tasks, agents, instances, projectId }: TaskBoardProps) {
  const [activeTask, setActiveTask] = useState<Task | null>(null);
  const [showNewTask, setShowNewTask] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const updateTask = useUpdateTask();
  const createTask = useCreateTask(projectId);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const tasksByStatus = (status: TaskStatus) =>
    tasks.filter((t) => t.status === status);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const task = tasks.find((t) => t.id === event.active.id);
    if (task) setActiveTask(task);
  }, [tasks]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setActiveTask(null);
    const { active, over } = event;
    if (!over) return;

    const taskId = active.id as string;
    const newStatus = (COLUMNS.find((c) => c.status === over.id)?.status ?? over.id) as TaskStatus;
    const task = tasks.find((t) => t.id === taskId);
    if (!task || task.status === newStatus) return;

    updateTask.mutate({ id: taskId, status: newStatus });
  }, [tasks, updateTask]);

  const handleCreateTask = async () => {
    if (!newTitle.trim()) return;
    await createTask.mutateAsync({ title: newTitle.trim() });
    setNewTitle('');
    setShowNewTask(false);
  };

  return (
    <div>
      {showNewTask && (
        <div className="mb-4 flex gap-2">
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreateTask()}
            placeholder="Task title..."
            className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
            autoFocus
          />
          <button
            onClick={handleCreateTask}
            disabled={createTask.isPending}
            className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
          >
            Add
          </button>
          <button
            onClick={() => { setShowNewTask(false); setNewTitle(''); }}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
        </div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="flex gap-4 overflow-x-auto pb-4">
          {COLUMNS.map(({ status, label, color }) => (
            <Column
              key={status}
              status={status}
              label={label}
              color={color}
              tasks={tasksByStatus(status)}
              agents={agents}
              instances={instances}
              onAddTask={status === 'todo' ? () => setShowNewTask(true) : undefined}
            />
          ))}
        </div>

        <DragOverlay>
          {activeTask && <TaskCard task={activeTask} />}
        </DragOverlay>
      </DndContext>
    </div>
  );
}
